// src/pages/DashboardPages/EmailSettings.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../../assets/logo.png";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// If baseURL already ends with /api => use "email-settings"
// else => use "api/email-settings"
const EMAIL_SETTINGS_ENDPOINT = API_BASE_URL.endsWith("/api")
  ? "email-settings"
  : "api/email-settings";

const api = axios.create({ baseURL: API_BASE_URL });

// Keep your auth header (fine even if backend doesn't use it)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const EmailSettings = () => {
  const navigate = useNavigate();

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(false);

  // IMPORTANT: keep real password for edit (do not display it)
  const [existingPassword, setExistingPassword] = useState("");

  const [formData, setFormData] = useState({
    id: null,
    email_id: "",
    email_password: "",
    service: "gmail",
    send_to: "",
  });

  // ✅ NEW: State for managing multiple recipient emails
  const [recipientEmails, setRecipientEmails] = useState([""]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await api.get(EMAIL_SETTINGS_ENDPOINT);
      setSettings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching email settings:", err);
      setError("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ NEW: Handle recipient email change
  const handleRecipientChange = (index, value) => {
    const newEmails = [...recipientEmails];
    newEmails[index] = value;
    setRecipientEmails(newEmails);
  };

  // ✅ NEW: Add new recipient email field
  const addRecipientField = () => {
    setRecipientEmails([...recipientEmails, ""]);
  };

  // ✅ NEW: Remove recipient email field
  const removeRecipientField = (index) => {
    if (recipientEmails.length > 1) {
      const newEmails = recipientEmails.filter((_, i) => i !== index);
      setRecipientEmails(newEmails);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Backend requires email_id + email_password for POST
    if (!formData.email_id) {
      setError("Email ID is required");
      return;
    }

    // Add: password required
    if (!formData.id && !formData.email_password) {
      setError("Password is required");
      return;
    }

    // ✅ Combine recipient emails into comma-separated string
    const combinedRecipients = recipientEmails
      .map(email => email.trim())
      .filter(email => email.length > 0)
      .join(", ");

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        email_id: formData.email_id,
        service: formData.service || "gmail",
        send_to: combinedRecipients, // ✅ Use combined emails
        // Update: if user did not type a new password, reuse existingPassword
        email_password:
          formData.id
            ? (formData.email_password ? formData.email_password : existingPassword)
            : formData.email_password,
      };

      if (formData.id) {
        await api.put(`${EMAIL_SETTINGS_ENDPOINT}/${formData.id}`, payload);
        setSuccess("Email settings updated successfully!");
      } else {
        await api.post(EMAIL_SETTINGS_ENDPOINT, payload);
        setSuccess("Email settings added successfully!");
      }

      // reset
      setFormData({
        id: null,
        email_id: "",
        email_password: "",
        service: "gmail",
        send_to: "",
      });
      setExistingPassword("");
      setRecipientEmails([""]); // ✅ Reset recipient fields
      setShowForm(false);

      await fetchSettings();
    } catch (err) {
      console.error("Error saving email settings:", err);
      setError(err.response?.data?.message || "Failed to save email settings");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    // Your backend GET returns real email_password.
    // Store it in state, but DO NOT show it in the input.
    setExistingPassword(setting.email_password || "");

    setFormData({
      id: setting.id,
      email_id: setting.email_id || "",
      email_password: "", // keep blank; user can enter new one if they want
      service: setting.service || "gmail",
      send_to: setting.send_to || "",
    });

    // ✅ Split comma-separated emails into array for editing
    if (setting.send_to) {
      const emails = setting.send_to.split(',').map(email => email.trim()).filter(email => email.length > 0);
      setRecipientEmails(emails.length > 0 ? emails : [""]);
    } else {
      setRecipientEmails([""]);
    }

    setShowForm(true);
    setError("");
    setSuccess("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this email configuration?")) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await api.delete(`${EMAIL_SETTINGS_ENDPOINT}/${id}`);
      setSuccess("Email settings deleted successfully!");
      await fetchSettings();
    } catch (err) {
      console.error("Error deleting email settings:", err);
      setError(err.response?.data?.message || "Failed to delete email settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center justify-between gap-3">
              <img src={logo} alt="HPR Infra Logo" className="h-10 sm:h-12 md:h-14 object-contain" />
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base w-full sm:w-auto"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
  {/* Header Section */}
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
    <div>
      <h1 className="text-3xl font-semibold text-gray-900">Email Settings</h1>
      <p className="text-gray-600 text-sm mt-1">
        Manage sender credentials and recipients for contact form emails.
      </p>
    </div>
    <button
      onClick={() => {
        setFormData({
          id: null,
          email_id: "",
          email_password: "",
          service: "gmail",
          send_to: "",
        });
        setExistingPassword("");
        setRecipientEmails([""]);
        setShowForm(!showForm);
        setError("");
        setSuccess("");
      }}
      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-all w-full sm:w-auto"
    >
      {showForm ? "Cancel" : "Add New"}
    </button>
  </div>

  {/* Alerts */}
  {success && (
    <div className="mb-4 rounded-md bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm">
      {success}
    </div>
  )}
  {error && (
    <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
      {error}
    </div>
  )}

  {/* Add/Edit Form */}
  {showForm && (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        {formData.id ? "Edit Configuration" : "Add New Configuration"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sender and Password */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sender Email *
            </label>
            <input
              type="email"
              name="email_id"
              value={formData.email_id}
              onChange={handleChange}
              placeholder="sender@gmail.com"
              required
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Password{" "}
              <span className="text-xs text-gray-500">
                (Create a Google App Password and paste it here.)
              </span>
            </label>
            <input
              type="password"
              name="email_password"
              value={formData.email_password}
              onChange={handleChange}
              placeholder={formData.id ? "Leave blank to keep existing" : "Enter app password"}
              required={!formData.id}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none transition"
            />
            <p className="text-xs text-gray-500 mt-1">
              Don’t have an app password? Generate one{" "}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                here
              </a>
              .
            </p>
          </div>
        </div>

        {/* Service and Recipients */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Service</label>
            <select
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none transition"
            >
              <option value="gmail">Gmail</option>
              <option value="outlook">Outlook</option>
              <option value="yahoo">Yahoo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Emails *
            </label>
            <div className="space-y-2">
              {recipientEmails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleRecipientChange(index, e.target.value)}
                    placeholder={`Recipient ${index + 1}`}
                    required={index === 0}
                    className="flex-1 border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none transition"
                  />
                  {recipientEmails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRecipientField(index)}
                      className="bg-rose-100 text-rose-600 hover:bg-rose-200 p-2 rounded-lg transition"
                    >
                      ✕
                    </button>
                  )}
                  {index === recipientEmails.length - 1 && (
                    <button
                      type="button"
                      onClick={addRecipientField}
                      className="bg-green-100 text-green-600 hover:bg-green-200 p-2 rounded-lg transition"
                    >
                      +
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition disabled:bg-gray-400"
        >
          {loading ? "Saving..." : formData.id ? "Update Settings" : "Save Settings"}
        </button>
      </form>
    </div>
  )}

  {/* Table */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
    <table className="min-w-full text-left text-sm">
      <thead className="bg-blue-600 text-white">
        <tr>
          <th className="px-6 py-4 font-semibold">Sender Email</th>
          <th className="px-6 py-4 font-semibold">Recipient(s)</th>
          <th className="px-6 py-4 font-semibold">Service</th>
          <th className="px-6 py-4 font-semibold">Created</th>
          <th className="px-6 py-4 font-semibold text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading && settings.length === 0 ? (
          <tr>
            <td colSpan="5" className="text-center py-8 text-gray-500">
              Loading...
            </td>
          </tr>
        ) : settings.length === 0 ? (
          <tr>
            <td colSpan="5" className="text-center py-8 text-gray-500">
              No email settings found
            </td>
          </tr>
        ) : (
          settings.map((setting, index) => (
            <tr key={setting.id} className="border-t hover:bg-gray-50 transition">
              <td className="px-6 py-4 font-medium text-gray-800">{setting.email_id}</td>
              <td className="px-6 py-4 text-gray-700">{setting.send_to}</td>
              <td className="px-6 py-4 text-blue-700 capitalize">{setting.service}</td>
              <td className="px-6 py-4 text-gray-500">
                {new Date(setting.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(setting)}
                    className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-200 text-xs font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(setting.id)}
                    className="bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg hover:bg-rose-200 text-xs font-medium"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
</main>

    </div>
  );
};

export default EmailSettings;